/**
 * SiloSurfaceTunePanel — silo-scoped twin of LLMRoutingPanel.
 *
 * Renders the 8 LLM surfaces as a grid; each card has a Tune button that opens
 * a modal hosting <ModelOutputPanel> with the silo-scoped tune values. Save
 * flushes to /api/silos/{silo_id}/surface-tune/{surface_id}.
 *
 * Cascade visualization:
 *  - "Silo override" pill   → this silo set its own value
 *  - "Platform default" pill → falls through to the platform tune doc
 *  - "Inheriting" pill       → falls through to the routing cascade fallback
 *
 * iter-40 — replaces the legacy [Model & output length] + [LLM Routing]
 * cards in SiloSettings. The legacy fields (silos.model_name etc.) are
 * preserved on backend writes for backward compatibility with any silo that
 * still has them set.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Cpu, SlidersHorizontal, X, ArrowsCounterClockwise, Crosshair, Lightning, Brain, Wrench, ChatTeardropText, Export, Robot, Funnel } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import ModelOutputPanel from "@/components/ModelOutputPanel";
import StyleSelector from "@/components/AnswerStyleSelector";
import { usePlatformKeyGuard } from "@/lib/usePlatformKeyGuard";

const SURFACE_ICONS = {
  public_ask: Crosshair,
  compress: Lightning,
  tone_architect: ChatTeardropText,
  qa_probe: Brain,
  export_summary: Export,
  workbench_run: Wrench,
  ai_assistant: Robot,
  refinery: Funnel,
};

const SOURCE_PILL = {
  silo: { bg: "bg-[#FCEDE6]", fg: "text-[#A85E4A]", label: "Silo override" },
  platform: { bg: "bg-[#F0F1EE]", fg: "text-[#6B6A68]", label: "Platform default" },
  default: { bg: "bg-[#F0F1EE]", fg: "text-[#6B6A68]", label: "Inheriting" },
};

const fmtTokens = (n) => (n == null ? "—" : n.toLocaleString());

export default function SiloSurfaceTunePanel({ siloId, refreshSignal = 0 }) {
  const [data, setData] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tuneSurface, setTuneSurface] = useState(null);
  const [tuneValue, setTuneValue] = useState(null);
  const [tuneSnapshot, setTuneSnapshot] = useState(null);
  // Feb 14, 2026 — silo-level answer style (Public Q&A only). Fetched
  // once and refreshed after each save. Stored on the silo doc itself,
  // not in the surface-tune cascade.
  const [answerStyle, setAnswerStyle] = useState({ preset: "concise", custom: null });
  // May 25, 2026 — Platform-key voucher guard for non-Gemini selections.
  const platformKeyGuard = usePlatformKeyGuard();

  const reload = async () => {
    try {
      const [r, reg, siloDoc] = await Promise.all([
        api.get(`/silos/${siloId}/surface-tune`),
        registry ? Promise.resolve({ data: registry }) : api.get("/models/registry"),
        api.get(`/silos/${siloId}`),
      ]);
      setData(r.data);
      if (!registry) setRegistry(reg.data);
      setAnswerStyle({
        preset: siloDoc.data?.answer_style_preset || "concise",
        custom: siloDoc.data?.answer_style_custom || null,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load LLM tune for this silo.");
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [siloId, refreshSignal]);

  const overrideCount = useMemo(
    () => (data?.surfaces || []).filter((s) => s.has_silo_override).length,
    [data],
  );

  const openTune = (s) => {
    const seed = {
      model_name: s.model || s.surface_meta.default_model || "",
      max_output_tokens: s.max_output_tokens ?? 4096,
      default_max_output_tokens: s.default_max_output_tokens ?? s.max_output_tokens ?? 4096,
      provider: s.provider || s.surface_meta.default_provider || "gemini",
    };
    setTuneSurface(s.surface_meta);
    setTuneValue(seed);
    setTuneSnapshot(seed);
  };

  const closeTune = () => { setTuneSurface(null); setTuneValue(null); setTuneSnapshot(null); };

  const saveTune = async () => {
    if (!tuneSurface || !tuneValue) return;
    const provider = (() => {
      if (!tuneValue.model_name) return null;
      if (tuneValue.model_name.startsWith("gemini")) return "gemini";
      if (tuneValue.model_name.startsWith("gpt")) return "openai";
      if (tuneValue.model_name.startsWith("claude")) return "anthropic";
      return tuneValue.provider || "gemini";
    })();
    // May 25, 2026 — Platform-key guard. If the user just picked a
    // non-Gemini model (Claude/GPT/etc.), check that THIS silo's
    // voucher can afford at least $1 of platform spend before
    // committing. If voucher is too low, surface the guard popup
    // which lets them cancel or top up. The actual save runs inside
    // `doSave()` only after the guard clears.
    const doSave = async () => {
      setBusy(true);
      try {
        const payload = {
          max_output_tokens: tuneValue.max_output_tokens,
          default_max_output_tokens: tuneValue.default_max_output_tokens,
        };
        if (tuneValue.model_name) {
          payload.model = tuneValue.model_name;
          payload.provider = provider;
        }
        await api.patch(`/silos/${siloId}/surface-tune/${tuneSurface.id}`, payload);
        toast.success(`${tuneSurface.label} saved for this silo.`);
        if (provider && provider !== "gemini") {
          toast.warning(
            `Heads up — ${tuneValue.model_name} runs on platform credit, not BYOK. This silo's voucher will be charged per call.`,
            { duration: 8000 }
          );
        }
        await reload();
        closeTune();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Save failed");
      } finally { setBusy(false); }
    };
    platformKeyGuard.checkBeforeModelChange({
      siloId,
      newProvider: provider,
      providerLabel: tuneValue.model_name,
      minDollarsNeeded: 1,
      onConfirmed: doSave,
    });
  };

  const resetTuneModel = async () => {
    if (!tuneSurface) return;
    setBusy(true);
    try {
      await api.post(`/silos/${siloId}/surface-tune/${tuneSurface.id}/reset-model`);
      await reload();
      // Reload the modal so it reflects the new platform-default model.
      const fresh = (data?.surfaces || []).find((s) => s.surface === tuneSurface.id);
      setTuneValue((v) => ({
        ...v,
        model_name: fresh?.model || tuneSurface.default_model || "",
        provider: fresh?.provider || tuneSurface.default_provider || "gemini",
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const resetTuneSliders = async () => {
    if (!tuneSurface) return;
    setBusy(true);
    try {
      await api.post(`/silos/${siloId}/surface-tune/${tuneSurface.id}/reset-sliders`);
      await reload();
      // Snap modal back to factory defaults.
      setTuneValue((v) => ({
        ...v,
        max_output_tokens: 4096,
        default_max_output_tokens: 4096,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  // iter-40c — silo-wide bulk resets, governed only at silo scope.
  const resetAllSiloModels = async () => {
    if (!window.confirm("Reset every surface's LLM (model + provider) for THIS silo back to platform defaults? Slider state stays as-is.")) return;
    setBusy(true);
    try {
      await api.post(`/silos/${siloId}/surface-tune/reset-models`);
      await reload();
      toast.success("All LLMs reset for this silo.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const resetAllSiloSliders = async () => {
    if (!window.confirm("Reset every surface's sliders (output length, memory, bridge) for THIS silo back to defaults? Model choices stay as-is.")) return;
    setBusy(true);
    try {
      await api.post(`/silos/${siloId}/surface-tune/reset-sliders`);
      await reload();
      toast.success("All sliders reset for this silo.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  // May 12, 2026 — nuke EVERY override (model + sliders) for every surface
  // on this silo so it fully inherits platform defaults. Wipes the entire
  // surface_tune map. This is what the "X silo overrides" pill should
  // disappear after — the existing partial-reset buttons left behind
  // surface_tune docs with non-null fields, so the override flag stayed on.
  const clearAllSiloOverrides = async () => {
    if (!window.confirm("Clear EVERY silo override for this silo? Every surface will fall back to whatever the platform admin sets. Cannot be undone (but the platform defaults will repopulate).")) return;
    setBusy(true);
    try {
      // Hit /reset on every surface that currently has an override.
      const toReset = (data?.surfaces || []).filter((s) => s.has_silo_override);
      await Promise.all(toReset.map((s) =>
        api.post(`/silos/${siloId}/surface-tune/${s.surface}/reset`),
      ));
      await reload();
      toast.success(`Cleared ${toReset.length} silo override${toReset.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  };

  if (!data) {
    return <div className="text-sm text-[#6B6A68] py-6" data-testid="silo-surface-tune-loading">Loading LLM behavior…</div>;
  }

  return (
    <div className="bg-white border hairline rounded-lg p-6" data-testid="silo-surface-tune-panel">
      {platformKeyGuard.overlay}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-serif-display text-xl flex items-center gap-2">
            <Cpu size={18} weight="duotone" className="text-[#4A5D4E]" /> LLM behavior · per surface
          </h3>
          <p className="text-xs text-[#6B6A68] mt-1 max-w-2xl">
            Tune the model, output length, conversation memory, and summary bridge for each of this silo's 8 LLM surfaces independently.
            Anything you don't override falls through to platform defaults.
            {overrideCount > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#A85E4A] bg-[#FCEDE6] px-1.5 py-0.5 rounded">
                {overrideCount} silo override{overrideCount === 1 ? "" : "s"}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {overrideCount > 0 && (
            <button
              type="button"
              onClick={clearAllSiloOverrides}
              disabled={busy}
              data-testid="silo-surface-clear-all-overrides"
              title="Wipe every per-surface override so this silo inherits platform defaults again"
              className="text-xs px-3 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] flex items-center gap-1.5 disabled:opacity-50"
            >
              <ArrowsCounterClockwise size={12} /> Clear ALL silo overrides
            </button>
          )}
          <button
            type="button"
            onClick={resetAllSiloSliders}
            disabled={busy}
            data-testid="silo-surface-reset-all-sliders"
            className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1 text-[#6B6A68]"
          >
            <ArrowsCounterClockwise size={12} /> Reset all sliders to Default
          </button>
          <button
            type="button"
            onClick={resetAllSiloModels}
            disabled={busy}
            data-testid="silo-surface-reset-all-models"
            className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-1 text-[#6B6A68]"
          >
            <ArrowsCounterClockwise size={12} /> Reset All LLM&#39;s to default
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="silo-surface-grid">
        {data.surfaces.map((s) => {
          const Icon = SURFACE_ICONS[s.surface] || Robot;
          const pill = SOURCE_PILL[s.source] || SOURCE_PILL.default;
          return (
            <div
              key={s.surface}
              data-testid={`silo-surface-card-${s.surface}`}
              className={`border hairline rounded-md p-3.5 relative transition-colors ${s.has_silo_override ? "bg-[#FFF7F1] border-[#C16E5A]/40" : "bg-[#FDFCF8]"}`}
            >
              <button
                type="button"
                onClick={() => openTune(s)}
                disabled={busy}
                data-testid={`silo-surface-tune-${s.surface}`}
                className="absolute top-2.5 right-2.5 text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-md border hairline text-[#6B6A68] hover:text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1 bg-white"
              >
                <SlidersHorizontal size={11} weight="duotone" /> Tune
              </button>
              <div className="flex items-start gap-2.5 pr-16">
                <div className="w-8 h-8 rounded-md bg-[#F4F2EC] flex items-center justify-center shrink-0">
                  <Icon size={16} weight="duotone" className="text-[#1C1B1A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="font-medium text-sm text-[#1C1B1A] truncate">{s.surface_meta.label}</div>
                    <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded ${pill.bg} ${pill.fg}`}>
                      {pill.label}
                    </span>
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-[#6B6A68]">
                    <div className="truncate"><span className="text-[#1C1B1A] font-mono">{s.model || "—"}</span></div>
                    <div className="text-right tabular-nums">{fmtTokens(s.max_output_tokens)} tok</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-surface Tune modal */}
      {tuneSurface && tuneValue && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={closeTune}
          data-testid={`silo-tune-modal-${tuneSurface.id}`}
        >
          <div className="bg-[#FDFCF8] rounded-lg shadow-2xl max-w-2xl w-full mt-12 mb-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b hairline">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
                  Silo · per-surface tune · <span className="font-mono normal-case tracking-normal text-[#1C1B1A]">{tuneSurface.id}</span>
                </div>
                <h3 className="font-serif-display text-xl mt-0.5">{tuneSurface.label}</h3>
              </div>
              <button
                type="button"
                onClick={closeTune}
                aria-label="Close"
                data-testid={`silo-tune-modal-close-${tuneSurface.id}`}
                className="text-[#6B6A68] hover:text-[#1C1B1A] p-1.5 -m-1.5"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <ModelOutputPanel
                scope={`silo-surface-${tuneSurface.id}`}
                heading="Model & output length"
                subtitle={
                  <>
                    These dials apply only to this silo's <strong>{tuneSurface.label}</strong> surface. Other surfaces in this silo are unaffected; other silos are unaffected.
                    Anything you don't change keeps inheriting from the platform default.
                  </>
                }
                value={tuneValue}
                defaults={tuneSnapshot}
                registry={registry}
                busy={busy}
                onChange={setTuneValue}
                onSave={saveTune}
                onResetModel={resetTuneModel}
                onResetSliders={resetTuneSliders}
              />
              {/* Feb 14, 2026 — Answer Style picker. Public Q&A surface only.
                  Saves to silo doc itself via PATCH /silos/{id}; independent
                  of the surface-tune cascade. */}
              {tuneSurface.id === "public_ask" && (
                <div className="mt-5 pt-5 border-t border-[#E5E2DD]" data-testid="silo-public-ask-answer-style">
                  <h4 className="font-serif-display text-base mb-1 flex items-center gap-2 text-[#1C1B1A]">
                    Answer style
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] bg-[#F0F1EE] px-1.5 py-0.5 rounded">
                      Public Q&amp;A only
                    </span>
                  </h4>
                  <p className="text-[11px] text-[#6B6A68] mb-3 max-w-md">
                    Shapes how long answers run on this silo's public chat. Doesn't cap tokens — the LLM still gets its full thinking budget.
                  </p>
                  <StyleSelector
                    variant="answer"
                    preset={answerStyle.preset}
                    custom={answerStyle.custom}
                    onChange={async ({ preset, custom }) => {
                      // Optimistic update + persist immediately.
                      const prev = answerStyle;
                      setAnswerStyle({ preset, custom });
                      try {
                        await api.patch(`/silos/${siloId}`, {
                          answer_style_preset: preset,
                          answer_style_custom: custom,
                        });
                        toast.success("Answer style saved.");
                      } catch (e) {
                        setAnswerStyle(prev);
                        toast.error(e.response?.data?.detail || "Couldn't save answer style.");
                      }
                    }}
                    testidPrefix={`silo-${siloId}-public-ask`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
